import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Save, Search, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { StickyActionBar } from "@/components/layout/StickyActionBar";
import { formatCep, formatCnpj } from "@/lib/format";
import { buscarCep, saveNovaFranquia } from "./formApi";
import { franquiaSchema, type FranquiaFormValues } from "./formSchema";
import { ESTADOS_BR } from "@/lib/estados";

// Porte de app/controllers/franquia.controller.js +
// app/templates/franquia/form-franquia.html. Sem lookup de instituição (é
// uma lista livre de códigos digitados, não o <EntityLookup> da Fase 4) —
// "Convidar Instituição inicial" é só uma tag-list textual.
const CEP_REGEX = /^\d{5}-?\d{3}$/;

export default function FormFranquia() {
  const navigate = useNavigate();
  const [codigos, setCodigos] = useState<string[]>([]);
  const [novaInst, setNovaInst] = useState("");
  const [buscandoCep, setBuscandoCep] = useState(false);

  const form = useForm<FranquiaFormValues>({
    resolver: zodResolver(franquiaSchema),
    defaultValues: {
      nome: "",
      nomeFantasia: "",
      email: "",
      cnpj: "",
      telefone: "",
      cep: "",
      rua: "",
      bairro: "",
      municipio: "",
      estado: "",
      nomeUsuario: "",
      emailUsuario: "",
      usuario: "",
    },
  });

  const salvar = useMutation({
    mutationFn: (values: FranquiaFormValues) => saveNovaFranquia({ ...values, codigos }),
    onSuccess: () => {
      toast.success("Franquia cadastrada.");
      navigate("/Franquia/Consulta");
    },
  });

  async function handleBuscarCep() {
    const cep = form.getValues("cep") ?? "";
    if (!CEP_REGEX.test(cep)) {
      toast.error("CEP inválido!");
      return;
    }
    setBuscandoCep(true);
    try {
      const endereco = await buscarCep(cep);
      if (endereco.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      form.setValue("rua", endereco.logradouro ?? "");
      form.setValue("bairro", endereco.bairro ?? "");
      form.setValue("municipio", endereco.localidade ?? "");
      form.setValue("estado", endereco.uf ?? "");
    } catch {
      toast.error("Erro na busca de CEP");
    } finally {
      setBuscandoCep(false);
    }
  }

  function vincularInstituicao() {
    const nova = novaInst.trim();
    if (nova && !codigos.includes(nova)) {
      setCodigos((prev) => [...prev, nova]);
    }
    setNovaInst("");
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => salvar.mutate(values))} className="space-y-6 pb-20">
        <h1 className="text-lg font-semibold text-foreground">Cadastro de Franquia</h1>

        <h2 className="text-base font-semibold text-foreground">Informações da Franquia</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Razão Social</FormLabel>
                <FormControl>
                  <Input autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nomeFantasia"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome Fantasia</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cnpj"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CNPJ</FormLabel>
                <FormControl>
                  <Input {...field} onChange={(e) => field.onChange(formatCnpj(e.target.value))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cep"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CEP</FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <Input {...field} onChange={(e) => field.onChange(formatCep(e.target.value))} />
                  </FormControl>
                  <Button type="button" variant="outline" size="icon" disabled={buscandoCep} onClick={handleBuscarCep}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="rua"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Endereço</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="bairro"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bairro</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="municipio"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cidade</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="estado"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estado</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o Estado" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ESTADOS_BR.map((uf) => (
                      <SelectItem key={uf.value} value={uf.value}>
                        {uf.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        <h2 className="text-base font-semibold text-foreground">Usuário Principal da Franquia</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="nomeUsuario"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="emailUsuario"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="usuario"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Usuário</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <h2 className="text-base font-semibold text-foreground">Convidar Instituição Inicial</h2>
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Nova Instituição"
              value={novaInst}
              onChange={(e) => setNovaInst(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), vincularInstituicao())}
            />
            <Button type="button" onClick={vincularInstituicao}>
              Vincular
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {codigos.map((inst, index) => (
              <Badge key={`${inst}-${index}`} variant="secondary" className="gap-1">
                {inst}
                <button type="button" onClick={() => setCodigos((prev) => prev.filter((_, i) => i !== index))} title="remover">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        <StickyActionBar>
          <Button type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {salvar.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </StickyActionBar>
      </form>
    </Form>
  );
}
